import { ActionTaskInput } from '@automata/wfb-api';
import { OutputSlotFillingRuleInput } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { mapValues } from 'lodash';

import { getActionTaskLabwareSources } from './getActionTaskLabwareSources';
import {
  AllTaskProperties,
  InstrumentForLocation,
  TaskProperties,
} from '../../../types';
import { isStartOrStopShakeTask } from '../../../utils';
import {
  isSlotAllocatingInstrument,
  isTaskDoesNotSupportSlotAllocating,
  supportsSlotRanges,
} from '../../../utils/instrumentCapabilities';
import { LabwareAndSourceTaskByTaskId } from '../../selectors/selectLabwareAndSourceNodeByTaskId';
import type { LabwareByHandleIndexByTaskId } from '../../selectors/types';
import {
  MaybeActionTask,
  TimeEstimateValuesByTaskId,
} from '../../shared/types';
import addDefaultArgumentValues from '../addDefaultArgumentValues';
import { serializeHamiltonOutputSlot } from '../hamiltonOutputSlot';
import { isConnectorRef, ok } from '../utils';

export function getActionTask({
  taskDependencies,
  labwareIdsForTaskByTaskId,
  taskProperties,
  taskId,
  timeEstimateValuesByTaskId,
  instrument,
  labwareAndSourceTaskByTaskId,
  allTaskPropertiesById,
}: {
  taskDependencies: string[];
  labwareIdsForTaskByTaskId: LabwareByHandleIndexByTaskId;
  taskProperties: TaskProperties;
  taskId: string;
  timeEstimateValuesByTaskId: TimeEstimateValuesByTaskId;
  instrument: InstrumentForLocation;
  labwareAndSourceTaskByTaskId: LabwareAndSourceTaskByTaskId;
  allTaskPropertiesById: Record<string, AllTaskProperties>;
}): MaybeActionTask {
  // NOTE(LT, 21/11/2024): Maybe this will need to change when we work on labware positioning, in order to accommodate ranges etc.
  //
  // TODO(IL, 2025-01-21): we should probably parse `userSpecifiedSlot` to ParsedSlot[] or null
  // either here or earlier in the chain if possible, so that we don't have to parse it
  // both in getActionTaskLabwareSources and in the body of this fn below
  const userSpecifiedSlot =
    typeof taskProperties.taskParameters?.slot === 'string'
      ? taskProperties.taskParameters.slot
      : null;
  const isRangeString = (v: unknown): boolean =>
    typeof v === 'string' && /[,-]/.test(v.trim());
  // TODO(IL, 2024-04-09): flatMap as hack for no type-safe filter. Use more semantic util?
  const labwareIdsInTask = Object.values(
    labwareIdsForTaskByTaskId[taskId] ?? []
  ).flatMap((labwareId) => (labwareId === null ? [] : [labwareId]));
  const timeEstimate = timeEstimateValuesByTaskId[taskId];

  labwareIdsInTask.sort(); // sort to ensure deterministic order

  const labwareSources = getActionTaskLabwareSources({
    instrument,
    labwareIdsForTaskByTaskId,
    labwareIdsInTask,
    taskProperties,
    userSpecifiedSlot,
    labwareAndSourceTaskByTaskId,
    allTaskPropertiesById,
  });

  if (labwareSources.isError) {
    return labwareSources;
  }

  const labwareOutputs = Object.values(labwareSources.data).reduce<
    ActionTaskInput['labware_outputs']
  >((acc, labware, index) => {
    const labwareId = labware.labware_id;

    const startSpec = taskProperties.labwareIdsToInputSlots?.[labwareId] ?? '1';
    const endSpec =
      taskProperties.labwareIdsToOutputSlots?.[labwareId] ?? startSpec;

    const labwareSourceIsRange =
      typeof labwareSources.data[labwareId]?.destination_slot !== 'number';

    const hasRange =
      isRangeString(startSpec) ||
      isRangeString(endSpec) ||
      labwareSourceIsRange;
    const isRunLiquidHandlerTask =
      taskProperties.taskType === 'run_liquid_handler' ||
      taskProperties.taskType === 'run';

    let slot: OutputSlotFillingRuleInput | number;

    if (supportsSlotRanges(instrument.driverName) && isRunLiquidHandlerTask) {
      slot = serializeHamiltonOutputSlot(startSpec, endSpec);
    } else if (hasRange) {
      slot = { behavior: 'match_input' };
    } else if (
      isTaskDoesNotSupportSlotAllocating(
        instrument.driverName,
        taskProperties.taskType
      )
    ) {
      const userSpecifiedSlotNumber = Number.parseInt(userSpecifiedSlot ?? '');
      slot = Number.isFinite(userSpecifiedSlotNumber)
        ? userSpecifiedSlotNumber
        : index + 1;
    } else if (isSlotAllocatingInstrument(instrument.driverName)) {
      const chosen =
        taskProperties.labwareIdsToOutputSlots?.[labwareId] ??
        taskProperties.labwareIdsToInputSlots?.[labwareId] ??
        1;

      slot = typeof chosen === 'string' ? parseInt(chosen, 10) || 1 : chosen;
    } else {
      const userSpecifiedSlotNumber = Number.parseInt(userSpecifiedSlot ?? '');
      slot = Number.isFinite(userSpecifiedSlotNumber)
        ? userSpecifiedSlotNumber
        : index + 1;
    }

    return {
      ...acc,
      [labwareId]: {
        type: 'labware_output',
        labware_id: labwareId,
        slot,
      },
    };
  }, {});

  // NOTE(IL, 2024-04-09): as-is, it's entirely up to driver validation to check
  // whether `arguments.static` is OK or not. No validation here!
  const taskParametersWithDefaultValues = addDefaultArgumentValues(
    taskProperties.taskType,
    taskProperties.taskParameters,
    taskProperties.taskAppliedParameters
  );

  // TODO(AL, 05/12/2024): currently we don't support data inputs for driver args here. Only support pure static driver args and data outputs.
  const inputs: ActionTaskInput['inputs'] = taskParametersWithDefaultValues
    ? mapValues(taskParametersWithDefaultValues, (parameterValue, key) => {
        const parameterId = taskProperties.taskAppliedParameters?.[key];

        if (parameterId) {
          return { parameter_id: parameterId };
        }

        if (isConnectorRef(parameterValue)) {
          return parameterValue;
        }

        return { value: parameterValue };
      })
    : {};

  const { slot, ...inputsWithoutSlot } = inputs;
  const shouldIncludeSlot = isStartOrStopShakeTask(taskProperties.taskType);

  return ok({
    type: 'action',
    action: taskProperties.taskType,
    inputs: shouldIncludeSlot ? inputs : inputsWithoutSlot,
    dependencies: taskDependencies,
    description: taskProperties.nickname,
    id: taskId,
    instrument: {
      // NOTE(IL, 2024-05-14): this data specifies what instruments are *candidates* for this task.
      //
      // TODO(IL, 2024-05-13): consider using driver rather than instrument UUID for the `type`, to support load balancing
      type: instrument.id,
      // NOTE: when `mappings` is empty, it matches any instruments that share a `type`.
      // See https://github.com/automata-tech/linq-workflow-builder-api/blob/61a8feb279ccb8307fcad49079df02f34713a9bf/workflow_builder/translation/instrument.py#L7
      mappings: [],
    },

    labware_sources: labwareSources.data,
    labware_outputs: labwareOutputs,
    time_estimate: timeEstimate ?? 0,
  });
}
